"use client"

import { Stack } from "@mantine/core"
import classes from "./SideNavBar.module.css"
import { BASE_TEST_ID, PAGE_MENU_PATHS } from "@/components/generic/SideNavBar/constants"
import { NavbarOptionLiveMonitor, NavbarOptionMessenger, NavbarOptionSequences, NavbarOptionStats, NavbarOptionWelcome } from "@/components/generic/SideNavBar/components/clientComponents"
import { NavbarPowerDialerButton } from "@/components/generic/SideNavBar/components/NavbarPowerDialerButton/NavbarPowerDialerButton"
import { NavbarTemplatesButton } from "@/components/generic/SideNavBar/components/NavbarTemplatesButton/NavbarTemplatesButton"
import { NavbarButtonSupport } from "@/components/generic/SideNavBar/components/NavbarButtonSupport/NavbarButtonSupport"
import { useSelector } from "@legendapp/state/react"
import { syncStore$ } from "@/stores/sync.v2.store"
import { NavbarButtonFeatureBase } from "@/components/generic/SideNavBar/components/NavbarButtonFeatureBase/NavbarButtonFeatureBase"
import { usePathname } from "@/i18n/routing"
import { useTranslations } from "next-intl"
import { useModel } from "@/hooks/useModel"
import { NavbarContactsButton } from "@/components/generic/SideNavBar/components/NavbarContactsButton"
import type { TLoginUserData } from "@/schemas/user.schema"

/**
 * SideNavBar component renders a navigation sidebar with various navigation options.
 * It uses the current pathname to determine which navigation option is selected.
 *
 * @returns {JSX.Element} The rendered SideNavBar component.
 */
export function SideNavBar({ showSequenceFeature, userData }: { showSequenceFeature?: boolean | null; userData?: TLoginUserData }) {
  const pathName = usePathname()
  const syncShowSequenceFeature = useSelector(() => syncStore$?.account?.showSequenceFeature.get() ?? false)
  const t = useTranslations("sideNavBar")

  const IS_SEQUENCES_DISABLED = process.env.NEXT_PUBLIC_DISABLE_SEQUENCES === "true"
  const joinedShowSequenceFeature = IS_SEQUENCES_DISABLED ? false : (showSequenceFeature ?? syncShowSequenceFeature)
  const { User } = useModel()
  const hasMonitorAccess = userData ? Boolean(userData.user.isAdmin || userData.user.isOwner || userData.user.canAccessMonitor) : User.hasMonitorAccess

  return (
    <Stack component="nav" pb={"md"} className={classes.nav} justify="space-between" data-testid={`${BASE_TEST_ID}container`}>
      <Stack justify="center" gap={8}>
        {/* Welcome: /welcome */}
        <NavbarOptionWelcome isSelected={pathName === PAGE_MENU_PATHS.Welcome} label={t("navigation.welcome")} />

        {/* Messenger: / & /messenger */}
        <NavbarOptionMessenger isSelected={pathName === PAGE_MENU_PATHS.Messenger} label={t("navigation.messenger")} />

        {/* Power Dialer: /power-dialer */}
        <NavbarPowerDialerButton isSelected={pathName.includes(PAGE_MENU_PATHS.PowerDialer)} label={t("navigation.powerDialer")} />

        {/* Contacts: /contact/* */}
        {joinedShowSequenceFeature && (
          <NavbarContactsButton
            isSelected={pathName.includes(PAGE_MENU_PATHS.ContactListsLibrary) || pathName.includes(PAGE_MENU_PATHS.ContactPropertiesLibrary) || pathName.includes(PAGE_MENU_PATHS.ContactBulkEditor)}
            label={t("navigation.contacts")}
          />
        )}

        {/* Content: /templates/email/library & /templates/sms/library & /call/scripts */}
        <NavbarTemplatesButton
          showSequenceFeature={joinedShowSequenceFeature}
          isSelected={
            pathName.includes(PAGE_MENU_PATHS.EmailTemplatesLibrary) ||
            pathName.includes(PAGE_MENU_PATHS.SMSTemplatesLibrary) ||
            pathName.includes(PAGE_MENU_PATHS.CallScriptsLibrary) ||
            pathName.includes(PAGE_MENU_PATHS.EmailTemplatesComposer) ||
            pathName.includes(PAGE_MENU_PATHS.SMSTemplatesComposer)
          }
          label={t("navigation.templates")}
        />

        {/* Sequences: /sequences/library */}
        {joinedShowSequenceFeature && <NavbarOptionSequences isSelected={pathName.includes(PAGE_MENU_PATHS.SequenceListsLibrary)} label={t("navigation.sequences")} />}

        {/* Stats: /stats */}
        <NavbarOptionStats isSelected={pathName.includes(PAGE_MENU_PATHS.Stats)} label={t("navigation.stats")} />

        {/* Live Monitor: /live-monitor */}
        {hasMonitorAccess && <NavbarOptionLiveMonitor isSelected={pathName.includes(PAGE_MENU_PATHS.LiveMonitor)} label={t("navigation.liveMonitor")} />}
      </Stack>

      <Stack justify="center" gap={8}>
        <NavbarButtonFeatureBase label={t("featureBase.label")} />
        <NavbarButtonSupport label={t("intercom.label")} />
      </Stack>
    </Stack>
  )
}
